import FadeInContent from '@/components/Animation/FadeInContent';
import Paginator from '@/components/Custom/Paginator';
import SearchBar from '@/components/Custom/SearchBar';
import TableEmptyState from '@/components/Custom/TableEmptyState';
import TableNext from '@/components/Custom/TableNext';
import type {
  TableNextColumn,
  TableNextProps,
} from '@/components/Custom/TableNext.types';
import ActionButton from '@/components/UI/ActionButton';
import Section from '@/components/UI/Section';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import type usePaginatedList from '@/hooks/usePaginatedList';
import type { OrganizationAssignmentRow } from './OrganizationAssignmentDashboard.utils';

type OrganizationAssignmentDashboardProps = {
  actionContent?: React.ReactElement | null;
  assignmentCount: number;
  fetching: boolean;
  columns: TableNextColumn<OrganizationAssignmentRow>[];
  emptyDescription?: string;
  filterContent?: React.ReactElement | null;
  onChangeText: (value: string) => void;
  onPressCancelSearch: () => void;
  onPressRow?: TableNextProps<OrganizationAssignmentRow>['onPressRow'];
  paginated: ReturnType<typeof usePaginatedList>;
  emptyMessage: string;
  rows: OrganizationAssignmentRow[];
  searchPlaceholder: string;
  searchValue: string;
};

export default function OrganizationAssignmentDashboard({
  actionContent = null,
  assignmentCount,
  fetching,
  columns,
  emptyDescription,
  filterContent = null,
  onChangeText,
  onPressCancelSearch,
  onPressRow,
  paginated,
  emptyMessage,
  rows,
  searchPlaceholder,
  searchValue,
}: OrganizationAssignmentDashboardProps) {
  const hasHeaderActions = Boolean(actionContent || filterContent);

  return (
    <Section flexGrow={1}>
      <Section>
        {hasHeaderActions ? (
          <Box
            direction="horizontal"
            align="center"
            justify="flex-start"
            space="md"
            wrap
          >
            {actionContent}
            {filterContent}
          </Box>
        ) : null}
        <SearchBar
          value={searchValue}
          onChangeText={onChangeText}
          onPressCancel={onPressCancelSearch}
          placeholder={searchPlaceholder}
        />
      </Section>

      <FadeInContent
        fetching={fetching}
        flex={false}
        testID="BadgeAssignmentsFade"
      >
        <TableNext
          columns={columns}
          rows={rows}
          onPressRow={onPressRow}
          emptyContent={
            rows.length ? null : (
              <TableEmptyState message={emptyMessage}>
                {emptyDescription ? (
                  <Text size="sm" variant="typographySecondary" center>
                    {emptyDescription}
                  </Text>
                ) : null}
              </TableEmptyState>
            )
          }
        />
      </FadeInContent>
      <Paginator {...paginated} total={assignmentCount} />
    </Section>
  );
}

export function OrganizationAssignmentSelectionActions({
  onPressClearSelection,
  selectedCount,
  testIDPrefix,
}: {
  onPressClearSelection: () => void;
  selectedCount: number;
  testIDPrefix: string;
}) {
  if (!selectedCount) {
    return null;
  }

  return (
    <ActionButton
      forceLabel
      icon="close-outline"
      label="Clear"
      variant="secondary"
      testID={`${testIDPrefix}ClearSelectionButton`}
      onPress={onPressClearSelection}
    />
  );
}
